import CustomTable from "components/common/CustomTable";
import CustomTableLoader from "components/common/CustomTableLoader";
import CustomToast from "components/common/CustomToast";
import Dropdown from "components/common/Dropdown";
import EmptyStateComp from "components/common/EmptyStateComp";
import TextInput from "components/common/InputField/TextInput";
import MultiChannelSelect from "components/common/MultiChannelSelect";
import SynthTooltip from "components/common/SynthTooltip";
import { formatMoney, getDashboardChartData } from "helpers";
import useGetNonFinishedGoodsForecast from "hooks/Dashboard/useGetNonFinishedGoodsForecast";
import useGetSalesChannel from "hooks/Dashboard/useGetSalesChannel";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { truncate } from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import ResponsivePaginationComponent from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic.css";
import { dashboardActions } from "redux/Ldb/actions";
import { forecastPeriodOptions } from "variables/forecastPeriodOptions";

const ComponentsForecast = () => {
    const [durationFilter, setDurationFilter] = useState<string>("This year");
    const [startAndEndDate, setStartAndEndDate] = useState<string[]>([]);
    const [customValue, customOnChange] = useState("");
    const [channelFilter, setChannelFilter] = useState<string>("");
    const [refreshConfigList, setRefreshConfigList] = useState<boolean>(false);

    const [expandedRowIdx, setExpandedRowIdx] = useState<number>(-1);

    const dispatch = useAppDispatch();
    const {
        addingForecastConfig,
        addForecastConfigSuccess,
        fetchPinForecastConfigSuccess,
        fetchUnpinForecastConfigSuccess,
    } = useAppSelector((state) => state.dashboard);

    const { data: salesChannel, isFetching: salesChannelLoading } = useGetSalesChannel();

    const salesChannelOption = salesChannel
        ?.filter(
            (channel) =>
                channel?.toLowerCase() === "shopify" ||
                channel?.toLowerCase() === "retail" ||
                channel?.toLowerCase() === "amazon"
        )
        ?.map((channel) => {
            return {
                label: channel,
                value: channel,
                icon:
                    channel?.toLowerCase() === "shopify" ? (
                        <img
                            src="https://storage.googleapis.com/heysynth_files/synth_webapp/shopify_w87tn3.svg"
                            alt="shopify"
                            className="w-4 h-4"
                        />
                    ) : channel?.toLowerCase() === "amazon" ? (
                        <img
                            src="https://storage.googleapis.com/heysynth_files/synth_webapp/amazon_1_aeftdf.svg"
                            alt="amazon"
                            className="w-4 h-4"
                        />
                    ) : (
                        <i className="ri-store-2-fill text-y-10 text-base"></i>
                    ),
            };
        });

    const {
        data: nonFinishedGoodsData,
        isFetching: nonFinishedGoodsLoading,
        updatedPage: nonFinishedGoodsUpdatedPage,
        setUpdatedPage: nonFinishedGoodsSetUpdatedPage,
        pagination: nonFinishedGoodsPagination,
        handleDateRangeChange: nonFinishedGoodsHandleDateRangeChange,
        handleChannelChange: nonFinishedGoodsHandleChannelChange,
        handleCategoryChange: nonFinishedGoodsHandleCategoryChange,
        handleProductChange: nonFinishedGoodsHandleProductChange,
        productIds: nonFinishedGoodsProductIds,
        handleSearch: nonFinishedGoodsHandleSearch,
        handleDebouncedSearch: nonFinishedGoodsHandleDebouncedSearch,
        search: nonFinishedGoodsSearch,
        setEndDate,
        startDate,
        endDate,
    } = useGetNonFinishedGoodsForecast(false, refreshConfigList);

    const componentTableHeader = [
        {
            title: null,
            widthClass: "w-[50px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">Component</span>

                    <SynthTooltip
                        content={
                            "Individual materials or ingredients used to produce finished goods. Components are tracked separately to ensure accurate forecasting and procurement."
                        }
                        divId={"component-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Current stock level
                    </span>

                    <SynthTooltip
                        content={
                            "The actual quantity of the product currently available in stock for sale or use at this moment."
                        }
                        divId={"current-stock-level-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted qty in use
                    </span>

                    <SynthTooltip
                        content={
                            "The estimated amount of this component expected to be consumed in the production of finished goods, based on current demand forecasts and planned manufacturing activity."
                        }
                        divId={"forecasted-qty-in-use-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted needs
                    </span>

                    <SynthTooltip
                        content={
                            "The estimated amount of the product required to meet anticipated demand during the forecast period."
                        }
                        divId={"forecasted-needs-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Stock deficit
                    </span>

                    <SynthTooltip
                        content={
                            "The number of units your inventory is short based on forecasted demand. This highlights how much stock needs to be replenished to meet future needs."
                        }
                        divId={"stock-deficit-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted cost
                    </span>

                    <SynthTooltip
                        content={
                            "The predicted total cost to fulfill the forecasted demand, based on the product's unit cost and the stock deficit."
                        }
                        divId={"forecasted-cost-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
    ];

    const componentTableBody = nonFinishedGoodsData?.map((item, idx) => {
        const stockDeficit =
            item?.productStockDetails?.stockLevel - item?.forecast?.forecastedNeeds;
        const isPinned = item?.isPinned;

        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 flex justify-center items-center text-xl text-slate-600  py-2`}
                    >
                        <i
                            className={`ri-star-fill text-center cursor-pointer ${
                                isPinned ? "text-[#FCD34D]" : "text-[#E2E8F0] "
                            }`}
                            onClick={() => {
                                if (isPinned) {
                                    handleUnpinInventory(item?._id);
                                } else {
                                    handlePinInventory(item?._id);
                                }
                            }}
                        ></i>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">{item?.sku}</p>

                            <p className="">{item?.productName}</p>
                        </div>
                    </div>
                ),
                expandedRowAction: () => {
                    console.log("clicked");
                    idx === expandedRowIdx ? setExpandedRowIdx(-1) : setExpandedRowIdx(idx);
                    // setDate(snapshot?.date);
                },
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                {formatMoney()?.format(item?.productStockDetails?.stockLevel)}{" "}
                                {item?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                {formatMoney()?.format(item?.forecast?.forecast)}{" "}
                                {item?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p
                                className={`${
                                    item?.forecast?.forecastedNeeds
                                        ? "text-red-500"
                                        : "text-slate-500"
                                } text-xs `}
                            >
                                {item?.forecast?.forecastedNeeds === 0 ? (
                                    <span className="text-green-500">Sufficent</span>
                                ) : (
                                    formatMoney()?.format(item?.forecast?.forecastedNeeds) +
                                    " " +
                                    (item?.productStockDetails?.unitOfMeasurement || "")
                                )}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p
                                className={` text-xs ${
                                    String(stockDeficit)[0] === "-"
                                        ? "text-red-500"
                                        : "text-slate-500"
                                }`}
                            >
                                {String(stockDeficit)[0] === "-" ? (
                                    formatMoney()?.format(String(stockDeficit)?.slice(1)) +
                                    " " +
                                    (item?.productStockDetails?.unitOfMeasurement || "")
                                ) : (
                                    <span className="text-green-500">Sufficent</span>
                                )}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                ${formatMoney()?.format(item?.forecast?.forecastedCost)}
                            </p>
                        </div>
                    </div>
                ),
            },
        ];
    });

    const expandedTableHeader = [
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Associated SKUs/products
                    </span>

                    <SynthTooltip
                        content={
                            "Finished goods (SKUs) that have this particular product as one of its components."
                        }
                        divId={"associated-product-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted qty
                    </span>

                    <SynthTooltip
                        content={
                            "The estimated number of units expected to be needed or used during the forecast period."
                        }
                        divId={"forecasted-qty-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
    ];

    const expandedTableBody = nonFinishedGoodsData?.[expandedRowIdx]?.inventoriesUsedIn?.map(
        (item, idx) => {
            return [
                {
                    content: (
                        <div
                            key={idx}
                            className={`!font-rocGroteskMedium border-slate-100 text-sm  py-2`}
                        >
                            <div>
                                <p className=" text-sm ">{item?.inventory?.sku}</p>

                                <p className="text-sm text-slate-500">
                                    {item?.inventory?.productName}
                                </p>
                            </div>
                        </div>
                    ),
                },
                {
                    content: (
                        <div
                            key={idx}
                            className={`!font-rocGroteskMedium border-slate-100 text-sm  py-2`}
                        >
                            <div>
                                <p className=" text-sm ">
                                    {formatMoney()?.format(
                                        item?.estinmatedSalesForecast?.forecastedSale
                                    )}{" "}
                                    {nonFinishedGoodsData?.[expandedRowIdx]?.productStockDetails
                                        ?.unitOfMeasurement || ""}
                                </p>
                            </div>
                        </div>
                    ),
                },
            ];
        }
    );

    useEffect(() => {
        if (Boolean(addForecastConfigSuccess)) {
            toast.custom((t) => (
                <CustomToast t={t} message={"Configurations saved successfully"} type={"success"} />
            ));
            dispatch(dashboardActions.resetAddForecastConfig());
        }
    }, [dispatch, addForecastConfigSuccess]);

    useEffect(() => {
        if (durationFilter === "custom" && customValue[0] !== customValue[1]) {
            const dates = [
                moment(customValue?.[0]).format("MMM DD YYYY"),
                moment(customValue?.[1]).format("MMM DD YYYY"),
            ];

            setStartAndEndDate([dates?.[0], dates?.[1]]);
        }
    }, [durationFilter, customValue]);

    const handlePinInventory = (inventoryId: string) => {
        dispatch(dashboardActions.pinForecastConfig(inventoryId));
    };

    const handleUnpinInventory = (inventoryId: string) => {
        dispatch(dashboardActions.unpinForecastConfig(inventoryId));
    };

    useEffect(() => {
        if (!!fetchPinForecastConfigSuccess) {
            setRefreshConfigList((prevValue) => !prevValue);
        }
    }, [fetchPinForecastConfigSuccess]);

    useEffect(() => {
        if (!!fetchUnpinForecastConfigSuccess) {
            setRefreshConfigList((prevValue) => !prevValue);
        }
    }, [fetchUnpinForecastConfigSuccess]);

    return (
        <div>
            <div className="relative lg:border lg:border-slate-200 lg:rounded-xl py-[15px] lg:px-5 lg:mt-[16px]">
                <div className="lg:flex justify-between">
                    <div className="flex items-center space-x-6">
                        <p className="text-g-75 font-rocGroteskMedium text-sm lg:text-lg mb-4 lg:mb-0">
                            Component Forecast
                        </p>
                    </div>

                    <div className="">
                        <div className="flex sm:space-x-2.5  max-sm:justify-between max-sm: items-end border-b border-b-slate-200 sm:border-b-0">
                            <MultiChannelSelect
                                value={channelFilter}
                                salesChannel={salesChannelOption?.map((item) => item.value)}
                                isLoading={salesChannelLoading}
                                onChange={(value) => {
                                    setChannelFilter(value);
                                    nonFinishedGoodsHandleChannelChange(value);
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-4">
                    <div className="flex items-center justify-between py-[13px] border-t">
                        <div>
                            <Dropdown
                                handleChange={(name, value) => {
                                    setEndDate(value);
                                }}
                                dropdown={
                                    <div className="flex items-center justify-between h-[36px] rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 border-0 sm:!border sm:!border-slate-100">
                                        <div
                                            className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block whitespace-nowrap w-fit ${
                                                channelFilter && "capitalize"
                                            }`}
                                        >
                                            {startDate && endDate
                                                ? `${moment(endDate).diff(
                                                      moment(startDate),
                                                      "months"
                                                  )} months (${moment(startDate).format(
                                                      "MMM YYYY"
                                                  )} - ${moment(endDate).format("MMM YYYY")})`
                                                : "6 months"}
                                        </div>
                                        <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                                    </div>
                                }
                                dropdownItemsClasses="!px-0 !py-0"
                                dropdownClassName="!mb-10 !-mt-7"
                                dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-[200px] !w-[190px] !min-w-min !left-0 !z-[1400]`}
                                name="valueRange"
                                dropdownOptions={forecastPeriodOptions}
                            />
                        </div>

                        <div>
                            <TextInput
                                name="search"
                                type="text"
                                value={nonFinishedGoodsSearch}
                                onChange={nonFinishedGoodsHandleSearch}
                                onInput={nonFinishedGoodsHandleDebouncedSearch}
                                inputPlaceholder={"Search products"}
                                inputClassName={
                                    "!h-[36px]  pl-[0px] !mb-0 !font-rocGroteskMedium placeholder:!text-slate-400 text-[13px] "
                                }
                                leftIcon={
                                    <i className="ri-search-line text-slate-400 mt-[2px]"></i>
                                }
                                inputContainerClassname={
                                    "!rounded-[4px] !bg-slate-50 !h-[36px] !w-[300px] !pl-[20px] !border !border-slate-200"
                                }
                            />
                        </div>
                    </div>

                    {nonFinishedGoodsLoading ? (
                        <CustomTableLoader tableHeader={componentTableHeader} />
                    ) : (
                        <div>
                            {nonFinishedGoodsData?.length > 0 ? (
                                <CustomTable
                                    tableBody={componentTableBody}
                                    tableHeader={componentTableHeader}
                                    isCellBordered={true}
                                    isCellSelectable={false}
                                    stickyHeader={true}
                                    headerContainerClass="!bg-slate-50 !border-slate-200"
                                    headerItemClass="font-rocGroteskMedium "
                                    tableClass="mb-12 !border-slate-200"
                                    isCollapseable={true}
                                    isCollapseableIconIndex={1}
                                    expandedRowIdx={expandedRowIdx}
                                    // allCellClick={true}
                                    // bodyItemClass="!border-slate-200"
                                    rowExpanded={
                                        <div className="relative z-[1300]">
                                            {/* {isLoadingGroup && snapshotsList?.length === 0 ? (
                                          <div className="flex justify-center my-4">
                                              <Loader color="gm-25" size={8} />
                                          </div>
                                      ) : ( */}
                                            <>
                                                <div className="pt-2.5 px-3 bg-slate-50">
                                                    {expandedTableBody?.length > 0 ? (
                                                        <div className="">
                                                            <CustomTable
                                                                tableBody={expandedTableBody}
                                                                tableHeader={expandedTableHeader}
                                                                isCellBordered={true}
                                                                isInifiniteScroll={false}
                                                                // isAllSelectable={true}
                                                                stickyHeader={true}
                                                                headerContainerClass="!bg-slate-50 !border-slate-200"
                                                                headerItemClass="font-rocGroteskMedium "
                                                                tableClass="my-3 !border-t-slate-200 !border-x-slate-200"
                                                                isScrollable={false}
                                                                tableContainerClass=""
                                                                // bodyItemClass="!border-slate-200"
                                                                expandedRowTClass="!z-[1300]"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="flex justify-center items-center h-full py-5">
                                                            <span className="text-center text-sm font-rocGroteskMedium">
                                                                No Associated Products available
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        </div>
                                    }
                                />
                            ) : (
                                <EmptyStateComp
                                    title="No data found"
                                    description="No data available"
                                />
                            )}
                        </div>
                    )}

                    <div className="flex justify-end mt-5">
                        <ResponsivePaginationComponent
                            current={Number(nonFinishedGoodsPagination?.currentPage)}
                            total={Number(nonFinishedGoodsPagination?.noOfPages)}
                            maxWidth={350}
                            onPageChange={(page) => {
                                nonFinishedGoodsSetUpdatedPage(page);
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ComponentsForecast;
